import { formatUnits, parseUnits, parseEther } from 'viem'

// ── Number Formatting ───────────────────────────────────────

export function fmt(n, d = 0) {
  if (n === null || n === undefined || isNaN(n)) return '-'
  let dp = Math.min(d, 2) // never show more than 2 decimal places, regardless of what a call site asks for...
  // ...unless that would round a genuinely nonzero value away to "0.00" - a tiny claimable
  // payout displaying as exactly zero reads as "nothing owed", which is actively misleading.
  if (n !== 0 && Number(n.toFixed(dp)) === 0) dp = d
  const fixed = n.toFixed(dp)
  const [int, dec] = fixed.split('.')
  const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dec !== undefined ? `${intFormatted}.${dec}` : intFormatted
}

/// `dp` defaults to 2 (cents) but accepts more precision for small
/// payout-style amounts, e.g. `fmtUsd(0.0031, 4)` -> "$0.0031" instead
/// of rounding to "$0.00".
export const fmtUsd = (n, dp = 2) => '$' + fmt(n, dp)
/// A tiny negative rate rounds to "-0.00%", and a minus sign on a zero reads as a warning the
/// number does no work to justify. Values that round to zero print as zero, sign dropped.
export const fmtPct = (n) => {
  const s = n.toFixed(2)
  return (s === '-0.00' ? '0.00' : s) + '%'
}
/// Every on-chain rate/threshold is in bps; this is `fmtPct(bps / 100)` with the `/100` boilerplate
/// (and any future rounding-policy change) in one place instead of ~70 call sites.
export const fmtBps = (bps) => fmtPct(bps / 100)

/// K/M/B-suffixed short form for hero-sized numbers (Stats page Overview/Governance cells) where
/// a full comma-grouped figure would overflow a fixed-width grid cell - "$18,353,418.23" becomes
/// "$18.35M". Values under 100,000 print in full via `fmt` instead (abbreviating "$42,318" to
/// "$42.3K" saves little width but loses real precision for a number that size).
export function fmtCompact(n, dp = 2) {
  if (n === null || n === undefined || isNaN(n)) return '-'
  const abs = Math.abs(n)
  if (abs < 100_000) return fmt(n, dp)
  const units = [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']]
  for (const [threshold, suffix] of units) {
    if (abs >= threshold) return (n / threshold).toFixed(dp) + suffix
  }
  return fmt(n, dp)
}
export const fmtUsdCompact = (n, dp = 2) => '$' + fmtCompact(n, dp)

/// For "Balance: X" / "Wallet: X" style displays fed by an already-`toNumber()`'d value where
/// the raw wei isn't conveniently at hand. Rounds DOWN, never to nearest, so the number shown
/// is always safe to type back into an amount field - `fmt()`'s normal half-up rounding can
/// round a true balance like 0.999999999999999998 UP to a displayed "1.00", and typing that
/// exact "1" back in then overshoots the real balance and the transfer reverts with
/// ERC20InsufficientBalance.
///
/// This is a PARTIAL fix, not a complete one: `toNumber()` (a plain JS `Number()` conversion)
/// already collapses a wei value THIS close to a round number into an exact float indistinguishable
/// from 1 before this function ever sees it - flooring a value that's already silently become
/// exactly `1` has nothing left to floor away. Prefer `fmtBalanceWei` below wherever the raw wei/
/// BigInt is available (every amount-input balance display in this app should be) - it works
/// directly from viem's `formatUnits` string output and never round-trips through a lossy float
/// at all, so it doesn't share this gap.
export function fmtBalance(n, d = 4) {
  if (n === null || n === undefined || isNaN(n)) return '-'
  const factor = 10 ** d
  const floored = Math.floor(n * factor) / factor
  const fixed = floored.toFixed(d)
  const [int, dec] = fixed.split('.')
  const intFormatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dec !== undefined ? `${intFormatted}.${dec}` : intFormatted
}

/// The real fix for "Balance: X" displays: formats a raw wei BigInt directly from viem's
/// `formatUnits` decimal STRING, truncating (not rounding) to `dp` places via string slicing -
/// never converting through a JS `Number()` at any point, so it can't silently collapse a
/// near-round value the way `fmtBalance`/`toNumber()` can. Always prefer this over `fmtBalance`
/// when the raw wei value is available (it usually already is, for the exact same MAX-button
/// read every amount form already does via `weiToInputString`).
export function fmtBalanceWei(wei, decimals = 18, dp = 4) {
  if (wei === null || wei === undefined) return '-'
  const full = formatUnits(wei, decimals) // exact decimal string, e.g. "0.999999999999999998"
  const [intPart, decPart = ''] = full.split('.')
  const truncatedDec = decPart.slice(0, dp).padEnd(dp, '0')
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dp > 0 ? `${intFormatted}.${truncatedDec}` : intFormatted
}

// ── Address Formatting ──────────────────────────────────────

export function truncAddr(a) {
  if (!a) return ''
  return `${a.slice(0, 6)}...${a.slice(-4)}`
}

// ── BigInt Helpers ───────────────────────────────────────────

export function toNumber(bigintVal, decimals = 18) {
  if (!bigintVal) return 0
  return Number(formatUnits(bigintVal, decimals))
}

export function toWei(amount, decimals = 18) {
  return parseUnits(String(amount), decimals)
}

/// Format a raw wei BigInt into a decimal string safe to round-trip
/// through parseUnits. Used by MAX buttons so the input reflects the
/// exact on-chain balance - skipping the Number → String conversion
/// that silently drops precision past ~15 digits and causes
/// ERC20InsufficientBalance reverts on "stake max" / "bid max".
export function weiToInputString(wei, decimals = 18) {
  if (!wei || wei === 0n) return '0'
  const s = formatUnits(wei, decimals)
  // strip trailing zeros in the fractional part, then a dangling dot
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s
}

export function toEthWei(amount) {
  return parseEther(String(amount))
}

// ── Polling intervals (ms). Use these everywhere ───────────

export const POLL_FAST = 10_000   // price-sensitive reads
export const POLL_SLOW = 30_000   // global state (changes once/day, 30s is plenty)
export const POLL_NONE = false    // fetch once, refetch manually after user actions

// ── Toast durations (ms) ────────────────────────────────────

export const TOAST_DURATION = 4_000
export const TOAST_FADE     = 400

// ── Validators ──────────────────────────────────────────────

export function validateAmount(val) {
  if (!val || val.trim() === '') return 'Enter an amount'
  const n = parseFloat(val)
  if (isNaN(n) || n <= 0) return 'Amount must be greater than 0'
  return ''
}

export function isValidAddress(addr) {
  return /^0x[0-9a-fA-F]{40}$/.test(addr)
}

// ── Misc ────────────────────────────────────────────────────

export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
export const delay = (ms) => new Promise((r) => setTimeout(r, ms))

// ── Borrow health zones ─────────────────────────────────────
// Shared by every LTV display across the app (both borrow markets' health bar, the
// pre-submit liquidation warning, LiquidationsSection's own risk badge) so "how close to
// liquidation" always means the same thing everywhere. 'danger' matches the margin
// LiquidationsSection already used on its own before this existed; 'caution' is an earlier
// heads-up band, not itself close to actual liquidation risk.
export function ltvZone(ltvPct, maxLtvPct) {
  if (!(maxLtvPct > 0)) return 'safe'
  if (ltvPct >= maxLtvPct * 0.9) return 'danger'
  if (ltvPct >= maxLtvPct * 0.75) return 'caution'
  return 'safe'
}

// ── Rank Badge System ──────────────────────────────────────

export function getRank(lifetimeUsdx) {
  if (lifetimeUsdx >= 100000) return { name: 'Titan', color: '#FFD700', icon: '\u265B' }
  if (lifetimeUsdx >= 10000) return { name: 'Diamond', color: '#00D4FF', icon: '\u25C6' }
  if (lifetimeUsdx >= 1000) return { name: 'Gold', color: '#FFD166', icon: '\u2605' }
  if (lifetimeUsdx >= 100) return { name: 'Silver', color: '#C0C0C0', icon: '\u2606' }
  if (lifetimeUsdx >= 1) return { name: 'Bronze', color: '#CD7F32', icon: '\u25CF' }
  return { name: 'Unranked', color: '#8A93A6', icon: '\u25CB' }
}
